<template>
  <div v-if="hasSocialAuth">
    <div> - {{ $t('or') }} -</div>
    <div class="social-wrapp justify-content-center m-4" :class="disabled ? 'disabled' : ''">
      <div v-if="googleKey" class="d-flex justify-content-center">
        <div id="g_id_onload" data-auto_prompt='false' :data-client_id="googleKey"
             data-locale="en"></div>
        <div ref="googleBtn"/>
      </div>
      <button v-if="facebookKey"
              class="btn social"
              @click="useAuthProvider($options.PROVIDERS.FACEBOOK)"
      >
        <facebook-icon/>
        <span>{{ $t(btnTitle, {provider: 'Facebook'}) }}</span>
      </button>
      <template v-if="appleKey">
        <div
          class="apple-login"
          id="appleid-signin"
          data-type="continue"
          data-color="white"
        />
      </template>
    </div>
  </div>
</template>

<script>
import {Providers} from 'universal-social-auth';
import {mapActions, mapMutations, mapState} from 'vuex';
import {AUTH_PROVIDERS, AUTH_TYPE} from '@/const';
import FacebookIcon from '../../assets/img/FacebookIcon';
import {HOME} from '@/pages';
import {ADD_TOAST} from '@/store/mutationTypes';
import {ADD_POPUP, GET_PROFILE, SOCIAL_AUTH} from '@/store/actionTypes';

export default {
  name: 'SocialAuth',
  components: {
    FacebookIcon,
  },
  PROVIDERS: AUTH_PROVIDERS,
  props: {
    type: {
      type: String,
      default: AUTH_TYPE.LOGIN,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    terms: {
      type: Boolean,
      default: false,
    },
    news: {
      type: Boolean,
      default: false,
    },
  },
  methods: {
    ...mapActions({
      getProfile: GET_PROFILE,
      socialLogin: SOCIAL_AUTH,
      addPopup: ADD_POPUP,
    }),
    ...mapMutations({
      addToast: ADD_TOAST,
    }),
    onReadyLogin() {
      location.reload();
    },
    onCredentialsResponse({
      token,
      provider,
    }) {
      this.onSocialLogin(provider, token, this.terms, this.news);
    },
    onSocialLogin(provider, token, terms = false, news = false) {
      this.socialLogin({
        provider: provider,
        platform: 'web',
        credentials: token,
        terms: terms,
        news: news,
      }).then(this.redirectAfterLogin).catch((err) => {
        if (err.error?.errors?.terms) {
          this.showPopup(token, provider);
          return;
        }
      });
    },
    redirectAfterLogin() {
      this.$storage.get('last_page') ?
        this.$router.replace({
          path: this.$storage.get('last_page'),
          query: this.$storage.get('last_page_query'),
        }).then(this.onReadyLogin) :
        this.$router.push(HOME).then(this.onReadyLogin);
    },
    useAuthProvider(provider) {
      const ProData = Providers[provider];
      const providers = {
        facebook: {
          clientId: this.channelSettings?.social['facebook-login-key'],
          state: 'api_key=' + this.$storage.get('api_key'),
        },
      };
      this.$Oauth.OverrideOptions.providers[provider] = {
        ...this.$Oauth.OverrideOptions.providers[provider],
        ...providers[provider],
      };
      this.$Oauth.authenticate(provider, ProData).then((_) => {
        this.$store.commit('CLEAR_CACHE');
        this.getProfile().then(this.redirectAfterLogin);
      }).catch((err) => {
        console.log(err);
      });
    },
    initApple() {
      try {
        if (!this.appleKey) return;
        window?.AppleID?.auth?.init?.({
          clientId: this.appleKey,
          scope: 'name email',
          redirectURI: window.location.origin,
          state: 'SignInUserAuthenticationRequest',
          usePopup: true,
        });

        const onAppleLoginSuccess = (event) => {
          const authData = event?.detail?.authorization ? event?.detail : event?.detail?.data;
          const token = authData?.authorization?.id_token;

          token && this.onCredentialsResponse({
            token,
            provider: 'apple',
          });
        };
        const onAppleLoginError = (event) => {
          const code = event.detail.error;
          const showError = code !== 'popup_closed_by_user';
          showError && this.addToast({
            text: `Something went wrong ${code}`,
            time: 5000,
            type: 'error',
          });
        };

        this.$once('hook:beforeDestroy', () => {
          document.removeEventListener('AppleIDSignInOnSuccess', onAppleLoginSuccess);
          document.removeEventListener('AppleIDSignInOnFailure', onAppleLoginError);
        });
        document.removeEventListener('AppleIDSignInOnSuccess', onAppleLoginSuccess);
        document.removeEventListener('AppleIDSignInOnFailure', onAppleLoginError);
        document.addEventListener('AppleIDSignInOnSuccess', onAppleLoginSuccess);
        document.addEventListener('AppleIDSignInOnFailure', onAppleLoginError);
      } catch (e) {
        console.error(e);
      }
    },
    initGoogle() {
      try {
        if (!this.googleKey) return;
        window.google?.accounts?.id.initialize({
          client_id: this.googleKey,
          callback: (res) => {
            if (!res.credential) return;
            this.onCredentialsResponse({
              token: res.credential,
              provider: 'google',
            });
          },
        });

        window.google?.accounts?.id.renderButton(this.$refs.googleBtn, {
          text: 'continue_with', // or 'signup_with' | 'continue_with' | 'signin'
          size: 'large', // or 'small' | 'medium' width: '290', // max width 400
          theme: 'outline', // or 'filled_black' | 'filled_blue'
          logo_alignment: 'center', // or 'center'
          width: 250,
        });
      } catch (e) {
        console.error(e);
      }
    },
    showPopup(token, provider) {
      this.addPopup({
        component: './TermsAcceptPopup.vue',
      }).then(() => {
        this.onSocialLogin(provider, token, true);
      }).catch((reason) => {
        console.log('reason', reason);
      });
    },
  },
  computed: {
    ...mapState({
      channelSettings: (state) => state.channel.settings,
    }),
    googleKey() {
      return this.channelSettings.social?.['google-login-key-web'];
    },
    facebookKey() {
      return this.channelSettings.social?.['facebook-login-key'];
    },
    appleKey() {
      return this.channelSettings.social?.['apple-login-key-web'];
    },
    btnTitle() {
      return this.type === AUTH_TYPE.LOGIN ? 'Login with' : 'Register with';
    },
    hasSocialAuth() {
      return Object.values(this.channelSettings.social || {})?.some((k) => k);
    },
  },
  metaInfo() {
    return {
      script: [
        {
          skip: !this.googleKey,
          vmid: 'google-js',
          src: '//accounts.google.com/gsi/client',
          async: true,
          callback: () => {
            this.initGoogle();
          },
        },
        {
          skip: !this.appleKey,
          vmid: 'apple-js',
          src: '//appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js',
          callback: () => {
            this.initApple();
          },
        },
      ],
    };
  },
  mounted() {
    if (this.appleKey) {
      this.initApple();
    }

    this.initGoogle();
  },
};
</script>

<style scoped lang="scss">
.social {
  display: flex;
  align-self: center;
  gap: .5rem;
}

.social-wrapp {
  text-transform: none;
  font-style: normal;
  display: flex;
  flex-direction: column;
  flex-wrap: wrap;
  align-items: center;

  > * {
    width: 250px;
    max-width: 250px;
    height: 2.5rem;
  }

  &.disabled {
    pointer-events: none;
    opacity: 0.5;
  }

  gap: 1rem;
}
</style>
